import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Button from "@mui/material/Button";
import { useICCCColors } from "./theme";
import { TIME_RANGES } from "../model/constants";

const Mini = ({ label, value, color, colors }) => (
  <MDBox
    sx={{
      flex: 1, textAlign: "center", py: 1.25, px: 1,
      bgcolor: colors.cardBg2,
      border: `1px solid ${color}40`,
      borderRadius: 1,
    }}
  >
    <MDTypography variant="caption" sx={{ color: colors.textSecondary, fontSize: 11 }}>
      {label}
    </MDTypography>
    <MDTypography variant="h5" fontWeight="bold" sx={{ color, lineHeight: 1.1 }}>
      {Number(value || 0).toLocaleString("en-IN")}
    </MDTypography>
  </MDBox>
);

export default function AlertsSummary({ summary, bySeverityGroup = [], timeRange = "all", onViewAll }) {
  const ICCC_COLORS = useICCCColors();
  const rangeLabel = (TIME_RANGES.find((r) => r.value === timeRange) || TIME_RANGES[0]).label;
  const th = {
    textAlign: "left",
    padding: "6px 8px",
    borderBottom: `1px solid ${ICCC_COLORS.divider}`,
    fontWeight: 600,
    color: ICCC_COLORS.textSecondary,
  };
  const td = {
    padding: "5px 8px",
    borderBottom: `1px solid ${ICCC_COLORS.divider}`,
    color: ICCC_COLORS.textSecondary,
  };
  return (
    <MDBox
      sx={{
        bgcolor: ICCC_COLORS.cardBg,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: ICCC_COLORS.shadow,
      }}
    >
      <MDTypography variant="h6" fontWeight="bold" sx={{ color: ICCC_COLORS.textPrimary }}>
        Alerts Summary{" "}
        <MDTypography component="span" variant="caption" sx={{ color: ICCC_COLORS.textMuted }}>
          ({rangeLabel})
        </MDTypography>
      </MDTypography>

      <MDBox display="flex" gap={1} mt={1}>
        <Mini label="Total"    value={summary?.total}    color={ICCC_COLORS.accent}   colors={ICCC_COLORS} />
        <Mini label="Critical" value={summary?.critical} color={ICCC_COLORS.critical} colors={ICCC_COLORS} />
        <Mini label="Warning"  value={summary?.warning}  color={ICCC_COLORS.warning}  colors={ICCC_COLORS} />
      </MDBox>

      <MDTypography variant="caption" fontWeight="medium" sx={{ mt: 2, mb: 0.5, color: ICCC_COLORS.textSecondary }}>
        Alerts by Severity and Group
      </MDTypography>

      <MDBox sx={{
        flex: 1, overflowY: "auto", maxHeight: 200,
        border: `1px solid ${ICCC_COLORS.divider}`, borderRadius: 1,
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0, background: ICCC_COLORS.cardBg2 }}>
            <tr>
              <th style={th}>Group</th>
              <th style={{ ...th, color: ICCC_COLORS.critical }}>Critical</th>
              <th style={{ ...th, color: ICCC_COLORS.warning }}>Warning</th>
              <th style={th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {bySeverityGroup.length === 0 && (
              <tr>
                <td colSpan={4} style={{ ...td, textAlign: "center", color: ICCC_COLORS.textMuted }}>
                  No alerts in last 24 h
                </td>
              </tr>
            )}
            {bySeverityGroup.map((r) => (
              <tr key={r.group}>
                <td style={td}>{r.group}</td>
                <td style={{ ...td, color: ICCC_COLORS.critical, fontWeight: 600 }}>{r.critical}</td>
                <td style={{ ...td, color: ICCC_COLORS.warning, fontWeight: 600 }}>{r.warning}</td>
                <td style={td}>{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </MDBox>

      <Button
        size="small"
        variant="outlined"
        onClick={onViewAll}
        sx={{
          mt: 1, alignSelf: "center", textTransform: "none",
          color: ICCC_COLORS.accent, borderColor: ICCC_COLORS.accent,
          "&:hover": { borderColor: ICCC_COLORS.accent, bgcolor: ICCC_COLORS.hoverBg },
        }}
      >
        View All Alerts
      </Button>
    </MDBox>
  );
}

AlertsSummary.propTypes = {
  summary: PropTypes.shape({
    total: PropTypes.number,
    critical: PropTypes.number,
    warning: PropTypes.number,
  }),
  bySeverityGroup: PropTypes.arrayOf(PropTypes.object),
  timeRange: PropTypes.string,
  onViewAll: PropTypes.func,
};

Mini.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  color: PropTypes.string,
  colors: PropTypes.object,
};
